import _ from "lodash";
import { replaceWithParentTests, getBfsTestIds } from ".";
import { handleBulletListPackageTests } from "../testAndPanelSelection/handleDuplicateTests";
import { getTestNode } from "../testAndPanelSelection/testSelection";

/**
 * Unified bundle replacing function for new_vw beta users
 * Works for both DBS and non-DBS versions without checking isDbsVersion
 * Fully dynamic - no hardcoded constants
 * 
 * Strategy:
 * 1. Filter out undefined elements from allSuggestions.
 * 2. Filter out discount panel suggestions (keep seasonal promos and bundles in bundleList).
 * 3. Remove duplicate suggestions based on bundleId.
 * 4. Normalize suggestions to ensure consistent structure.
 * 5. Create a global testList from testNodeList for efficient access.
 * 6. Separate normal bundles (fixed test combinations, no quantity field) and XChooseY bundles (with quantity field).
 * 7. Sort newValueIds for consistent processing.
 * 8. Find all possible combinations of normal bundles and XChooseY bundles using BFS in `findBundleCombinations`.
 *    - This involves a two-phase approach: first finding normal bundle combinations, then for each, trying to form XChooseY bundles from remaining tests.
 *    - XChooseY bundles have a zoomer pool (required) and an optional supplement pool, each with quantity requirements.
 *    - Normalize testIds to parent level before forming XChooseY bundles.
 *    - XChooseY panel pools can contain both parent and child level tests, so tests are matched if they (or their parent/children) are in the pool.
 *    - The logic picks the most expensive tests from each pool to maximize deduction.
 *    - It explores all combinations of different XChooseY bundles (not allowing multiple instances of the same bundle).
 * 9. Calculate the total price for each combination (normal + XChooseY) using `findCheapestBundleCombination`.
 *    - This function uses `replaceWithParentTests` to replace children with parents, optimizing the final price calculation.
 * 10. Identify and return the cheapest combination among all possibilities.
 * 
 * This handles cases where:
 * - Some tests form fixed bundles and the rest form XChooseY bundles
 * - Not forming a fixed bundle and using those tests for XChooseY is cheaper
 * - Multiple different XChooseY bundles can be applied together (but not multiple instances of the same bundle)
 * - Mixed combinations of normal and XChooseY bundles
 * - XChooseY bundles with only zoomer pool (no supplements)
 */
export const bundleReplacingFuncNewVw = (
  allSuggestions,
  newValueIds,
  testNodeList,
  bundleList
) => {
  allSuggestions = allSuggestions.filter(function(element) {
    return element !== undefined && !!element;
  });
  
  // Filter out discount panel suggestions for new_vw users (discount panels are hidden)
  // Keep seasonal promos (bundleId < 0) and xChooseY bundles (category === "xChooseY")
  allSuggestions = allSuggestions.filter(suggestion => {
    if (suggestion.bundleId < 0) {
      // Keep seasonal promos
      return true;
    }
    // For regular bundles, check if it exists in bundleList
    let bundle = bundleList && bundleList.find(b => b.bundleId === suggestion.bundleId);
    return bundle ? true : false;
  });
  
  // Remove duplicate suggestions based on bundleId
  const seenBundleIds = new Set();
  allSuggestions = allSuggestions.filter(suggestion => {
    if (seenBundleIds.has(suggestion.bundleId)) {
      return false;
    }
    seenBundleIds.add(suggestion.bundleId);
    return true;
  });
  
  // add bundle content ids to each suggestion
  allSuggestions = allSuggestions.map((i) => {
    return {
      bundleId: i.bundleId,
      name: i.name,
      originPrice: i.originPrice,
      finalPrice: i.finalPrice,
      bundleContentIds: i.bundleContentIds,
      quantity: i.quantity,
      supplementList: i.supplementList,
      supplementQuantity: i.supplementQuantity
    };
  });

  // console.log("allSuggestions", [...allSuggestions]);
  
  // Create global testList from testNodeList to avoid duplicate mapping
  const testList = testNodeList.map(node => node.value);
  
  // Separate normal bundles and XChooseY bundles
  let normalBundles = allSuggestions.filter(i => !i.quantity || i.quantity === 0);
  let xChooseYBundles = allSuggestions.filter(i => i.quantity && i.quantity > 0);

  // Sort testIds for consistent processing
  newValueIds = newValueIds.sort((a, b) => a - b);

  // Find all possible combinations of normal bundles and XChooseY bundles
  // Filtering logic is handled inside findBundleCombinations
  let bundleCombinations = findBundleCombinations(
    normalBundles,
    xChooseYBundles,
    newValueIds,
    testNodeList,
    testList
  );

  // console.log("normal bundles combinations", bundleCombinations);
  
  // Calculate price for each combination and find the cheapest
  let bestBundleCombination = findCheapestBundleCombination(
    bundleCombinations,
    newValueIds,
    testNodeList,
    testList
  );

  // Get remaining test IDs and applied bundles from best combination
  let finalRemainingTestIds = bestBundleCombination.remainingNewValues || newValueIds;
  let finalAppliedBundles = bestBundleCombination.appliedBundles || [];
  
  // Replace tests with their parents before returning
  finalRemainingTestIds = replaceWithParentTests(finalRemainingTestIds, testNodeList);
  handleBulletListPackageTests(testNodeList, finalRemainingTestIds);
  
  // Calculate final price
  let finalPrice = calculateTotalPrice(
    finalAppliedBundles,
    finalRemainingTestIds,
    testNodeList,
    testList
  );

  // console.log("finalRemainingTestIds", finalAppliedBundles);

  return {
    appliedBundles: finalAppliedBundles.map((bundle) => {
      // XChooseY bundles have quantity and appliedZoomers/appliedSupplements
      if (bundle.quantity && bundle.quantity > 0) {
        return {
          appliedBundleId: bundle.bundleId,
          bundlePrice: bundle.finalPrice,
          appliedZoomers: bundle.appliedZoomers || [],
          appliedSupplement: bundle.appliedSupplements || [],
        };
      } else {
        // Normal bundles
        return {
          appliedBundleId: bundle.bundleId,
          bundlePrice: bundle.finalPrice,
        };
      }
    }),
    remainingNewValues: finalRemainingTestIds,
    priceAfterAppliedBundle: finalPrice.toFixed(2),
  };
};

/**
 * Find all possible combinations of normal bundles and XChooseY bundles that can be formed from testIds
 */
function findBundleCombinations(
  normalBundles,
  xChooseYBundles,
  testIds,
  testNodeList,
  testList
) {
  // console.log("testIds", testIds);
  // console.log("normalBundles", [...normalBundles]);
  // console.log("xChooseYBundles", [...xChooseYBundles]);
  
  // Remove bundles where bundle price is larger than original price
  normalBundles = normalBundles.filter((i) => !i.originPrice || i.originPrice >= i.finalPrice);
  // Filter normal bundles to only those where all test IDs are in testIds
  normalBundles = normalBundles.filter(i => {
    return i.bundleContentIds && i.bundleContentIds.length > 0 && 
           i.bundleContentIds.every(id => testIds.includes(id));
  });

  // Filter XChooseY bundles - they need to have valid zoomer pool and quantity
  // Supplement pool is optional (e.g., "3 zoomers" bundle has no supplements)
  xChooseYBundles = xChooseYBundles.filter(bundle => {
    // Must have zoomer pool and quantity
    if (!bundle.bundleContentIds || bundle.bundleContentIds.length === 0 || !bundle.quantity || bundle.quantity === 0) {
      return false;
    }
    // If supplementQuantity is specified and > 0, must have supplementList
    if (bundle.supplementQuantity && bundle.supplementQuantity > 0) {
      return bundle.supplementList && bundle.supplementList.length > 0;
    }
    // Supplement pool is optional - bundle is valid if it has zoomer pool
    return true;
  });

  let candidateBundles = normalBundles.filter(bundle => {
    return bundle.bundleContentIds && bundle.bundleContentIds.length > 0;
  });

  // Use BFS to find all combinations of normal bundles
  let combinations = [];
  let queue = [{
    appliedBundles: [],
    remainingNewValues: [...testIds],
    remainingBundles: candidateBundles.filter(b => 
      b.bundleContentIds.every(id => testIds.includes(id))
    ),
    key: ""
  }];

  let visited = new Set();

  function getKey(obj) {
    let bundleIds = obj.appliedBundles.map(b => b.bundleId).sort((a, b) => a - b);
    return bundleIds.join(",");
  }

  // First, find all combinations of normal bundles
  while (queue.length > 0) {
    let current = queue.shift();
    let key = getKey(current);
    
    if (visited.has(key)) {
      continue;
    }
    visited.add(key);

    // If no more bundles can be applied, record this combination
    if (current.remainingBundles.length === 0) {
      combinations.push({
        appliedBundles: current.appliedBundles,
        remainingNewValues: current.remainingNewValues,
        key: key
      });
      continue;
    }

    // Try adding each remaining bundle
    for (let bundle of current.remainingBundles) {
      // Check if bundle can be applied (all its test IDs are in remaining tests)
      if (bundle.bundleContentIds.every(id => current.remainingNewValues.includes(id))) {
        let newRemainingTests = current.remainingNewValues.filter(
          id => !bundle.bundleContentIds.includes(id)
        );
        
        let newRemainingBundles = current.remainingBundles.filter(
          b => b.bundleId !== bundle.bundleId &&
               b.bundleContentIds.every(id => newRemainingTests.includes(id))
        );

        let newCombination = {
          appliedBundles: [...current.appliedBundles, bundle],
          remainingNewValues: newRemainingTests,
          remainingBundles: newRemainingBundles,
          key: ""
        };
        newCombination.key = getKey(newCombination);

        if (!visited.has(newCombination.key)) {
          queue.push(newCombination);
        }
      }
    }
  }

  // Also add option with no normal bundles applied
  combinations.push({
    appliedBundles: [],
    remainingNewValues: testIds,
    key: ""
  });

  // Now, for each combination of normal bundles, try to form XChooseY bundles from remaining tests
  let finalCombinations = [];
  
  for (let combo of combinations) {
    // Add the combination without XChooseY bundles
    finalCombinations.push({
      appliedBundles: [...combo.appliedBundles],
      remainingNewValues: combo.remainingNewValues,
      key: combo.key
    });

    // Try to form XChooseY bundles from remaining tests
    // Replace children with parent first so we match pool slots by parent when user has all children
    let remainingForXChooseY = replaceWithParentTests(combo.remainingNewValues, testNodeList);
    let xChooseYResults = tryFormXChooseYBundles(
      xChooseYBundles,
      remainingForXChooseY,
      testNodeList,
      testList
    );

    // For each possible XChooseY combination, create a new final combination
    for (let xChooseYResult of xChooseYResults) {
      finalCombinations.push({
        appliedBundles: [...combo.appliedBundles, ...xChooseYResult.appliedBundles],
        remainingNewValues: xChooseYResult.remainingNewValues,
        key: getKey({ appliedBundles: [...combo.appliedBundles, ...xChooseYResult.appliedBundles] })
      });
    }
  }

  // Remove duplicates
  let uniqueCombinations = new Map();
  finalCombinations.forEach(combo => {
    let key = getKey(combo);
    if (!uniqueCombinations.has(key)) {
      uniqueCombinations.set(key, combo);
    }
  });

  return Array.from(uniqueCombinations.values());
}

/**
 * Try to form XChooseY bundles from remaining test IDs
 * Returns array of possible XChooseY combinations (including empty and multiple bundles)
 * Uses BFS to explore all possible combinations of XChooseY bundles
 * Pool matching: if a parent is in the pool we use the parent (not individual children). When the pool
 * contains only children, we still replace with parent first: if the user has all of a parent's children
 * (and they are in the pool), we treat that as one slot filled by the parent even if the parent is not in the pool.
 */
function tryFormXChooseYBundles(
  xChooseYBundles,
  remainingTestIds,
  testNodeList,
  testList
) {
  let results = [];
  
  // Helper function to get test price
  function getTestPrice(testId, testList) {
    const test = testList.find(t => t.id === testId);
    return test ? test.price : 0;
  }


  // Helper function to get all test IDs from the pool that can be used based on availableTestIds.
  // availableTestIds are already normalized (children replaced with parent) before forming XChooseY.
  // Only add a test when the pool test itself is in availableTestIds. When we have the parent and
  // the pool has (any of) its children, we do not add the parent — we keep the parent and do not form the XChooseY.
  function getAvailableTestsForPool(availableTestIds, pool) {
    let availableTests = [];
    let availableTestSet = new Set(availableTestIds);

    for (let poolTestId of pool) {
      if (availableTestSet.has(poolTestId)) {
        availableTests.push(poolTestId);
      }
    }

    return availableTests;
  }

  // Helper function to get all lowest level descendant test IDs for a given test
  // This returns all leaf-level test IDs that would be consumed when selecting this test
  // Uses getBfsTestIds to recursively get all lowest level test IDs
  function getDescendantTestIds(testId, testNodeList) {
    const testNode = getTestNode(testNodeList, testId);
    if (!testNode) {
      return [testId];
    }
    
    // Use getBfsTestIds to get all lowest level test IDs
    // getBfsTestIds will always add at least one ID for a valid node:
    // - If node has no descendants, it adds the node's id
    // - If node has descendants, it recursively processes them and adds leaf nodes
    // - Special cases also add IDs
    let descendantTestIds = [];
    getBfsTestIds(testNode, descendantTestIds);
    
    return descendantTestIds;
  }

  // Helper function to try forming a single XChooseY bundle
  function tryFormSingleXChooseYBundle(bundle, availableTestIds, testNodeList, testList) {
    let zoomerPool = bundle.bundleContentIds || [];
    let supplementPool = bundle.supplementList || [];
    let zoomerQuantity = bundle.quantity || 0;
    let supplementQuantity = bundle.supplementQuantity || 0;
    // Find available tests in each pool
    let availableZoomers = getAvailableTestsForPool(availableTestIds, zoomerPool);
    let availableSupplements = getAvailableTestsForPool(availableTestIds, supplementPool);
    
    // Check if we have enough tests to form this XChooseY bundle
    // Supplement pool is optional - only check if supplementQuantity > 0
    let hasEnoughZoomers = availableZoomers.length >= zoomerQuantity;
    let hasEnoughSupplements = !supplementQuantity || supplementQuantity === 0 || 
                               availableSupplements.length >= supplementQuantity;
    
    if (hasEnoughZoomers && hasEnoughSupplements) {
      
      // Sort by price (descending) to pick expensive tests first for maximum deduction
      let sortedZoomers = availableZoomers
        .map(id => ({ id, price: getTestPrice(id, testList) }))
        .sort((a, b) => b.price - a.price);
      
      let sortedSupplements = availableSupplements
        .map(id => ({ id, price: getTestPrice(id, testList) }))
        .sort((a, b) => b.price - a.price);

      // Collect all valid combinations without overlap
      // We need to ensure no test selected for zoomer pool overlaps with tests selected for supplement pool
      let allValidCombinations = [];
      
      // Generate all possible combinations of zoomers and supplements
      // For zoomers, try all combinations of zoomerQuantity tests
      // For supplements, try all combinations of supplementQuantity tests
      // But ensure no overlap between zoomer and supplement selections
      
      // Helper to generate combinations (using function expression to avoid linter error)
      const getCombinations = (arr, k) => {
        if (k === 0) return [[]];
        if (k > arr.length) return [];
        
        let combinations = [];
        for (let i = 0; i <= arr.length - k; i++) {
          let rest = getCombinations(arr.slice(i + 1), k - 1);
          for (let combo of rest) {
            combinations.push([arr[i], ...combo]);
          }
        }
        return combinations;
      };
      
      // Pre-compute all descendant test IDs (lowest level) for each zoomer
      let zoomerDescendantTestIdsMap = new Map();
      for (let zoomer of sortedZoomers) {
        zoomerDescendantTestIdsMap.set(zoomer.id, new Set(getDescendantTestIds(zoomer.id, testNodeList)));
      }
      
      // Pre-compute all descendant test IDs (lowest level) for each supplement
      let supplementDescendantTestIdsMap = new Map();
      for (let supplement of sortedSupplements) {
        supplementDescendantTestIdsMap.set(supplement.id, new Set(getDescendantTestIds(supplement.id, testNodeList)));
      }
      
      let zoomerCombinations = getCombinations(sortedZoomers, zoomerQuantity);
      let allSupplementCombinations = supplementQuantity > 0 
        ? getCombinations(sortedSupplements, supplementQuantity)
        : [[]];
      
      for (let zoomerCombo of zoomerCombinations) {
        let selectedZoomerIds = zoomerCombo.map(z => z.id);
        
        // Find all descendant test IDs for this zoomer combination (lowest level)
        let usedZoomerTestIds = new Set();
        selectedZoomerIds.forEach(id => {
          let zoomerDescendantTestIds = zoomerDescendantTestIdsMap.get(id);
          if (zoomerDescendantTestIds) {
            zoomerDescendantTestIds.forEach(usedId => usedZoomerTestIds.add(usedId));
          }
        });
        
        // Filter out supplement combinations that have overlapping test IDs with zoomers
        let validSupplementCombinations = allSupplementCombinations.filter(supplementCombo => {
          // Check if any supplement in this combination has overlapping test IDs
          for (let supplement of supplementCombo) {
            let supplementDescendantTestIds = supplementDescendantTestIdsMap.get(supplement.id);
            if (supplementDescendantTestIds) {
              // Check if any descendant test ID from supplement overlaps with zoomer's descendant test IDs
              for (let usedId of supplementDescendantTestIds) {
                if (usedZoomerTestIds.has(usedId)) {
                  return false; // Overlap found, filter out this combination
                }
              }
            }
          }
          return true; // No overlap, keep this combination
        });
        
        // Collect all valid combinations
        for (let supplementCombo of validSupplementCombinations) {
          let selectedSupplementIds = supplementCombo.map(s => s.id);
          
          let totalPrice = zoomerCombo.reduce((sum, z) => sum + z.price, 0) +
                         supplementCombo.reduce((sum, s) => sum + s.price, 0);
          
          allValidCombinations.push({
            zoomers: selectedZoomerIds,
            supplements: selectedSupplementIds,
            totalPrice: totalPrice
          });
        }
      }
      
      // Find the best combination (highest total price)
      if (allValidCombinations.length > 0) {
        let bestSelection = allValidCombinations.reduce((best, current) => {
          return current.totalPrice > best.totalPrice ? current : best;
        });
        
        // Create the XChooseY bundle object
        let appliedXChooseYBundle = {
          bundleId: bundle.bundleId,
          name: bundle.name,
          finalPrice: bundle.finalPrice,
          quantity: zoomerQuantity,
          supplementQuantity: supplementQuantity,
          appliedZoomers: bestSelection.zoomers,
          appliedSupplements: bestSelection.supplements
        };

        // Calculate remaining tests after applying this XChooseY bundle
        // Use pre-calculated descendant test IDs maps; include slot IDs (parent or leaf) so normalized parents are removed
        let allUsedTestIds = new Set();
        bestSelection.zoomers.forEach(id => {
          allUsedTestIds.add(id);
          let zoomerDescendantTestIds = zoomerDescendantTestIdsMap.get(id);
          if (zoomerDescendantTestIds) {
            zoomerDescendantTestIds.forEach(usedId => allUsedTestIds.add(usedId));
          }
        });
        bestSelection.supplements.forEach(id => {
          allUsedTestIds.add(id);
          let supplementDescendantTestIds = supplementDescendantTestIdsMap.get(id);
          if (supplementDescendantTestIds) {
            supplementDescendantTestIds.forEach(usedId => allUsedTestIds.add(usedId));
          }
        });

        let newRemainingTests = availableTestIds.filter(id => !allUsedTestIds.has(id));

        return {
          appliedBundle: appliedXChooseYBundle,
          remainingTests: newRemainingTests
        };
      }
    }

    return null;
  }

  // Use BFS to explore all combinations of XChooseY bundles
  let queue = [{
    appliedBundles: [],
    remainingTests: [...remainingTestIds]
  }];

  let visited = new Set();

  while (queue.length > 0) {
    let current = queue.shift();
    
    // Create a key for visited states
    let bundleIds = current.appliedBundles.map(b => b.bundleId).sort((a, b) => a - b).join(',');
    let testIdsKey = current.remainingTests.sort((a, b) => a - b).join(',');
    let stateKey = `${bundleIds}|${testIdsKey}`;

    if (visited.has(stateKey)) {
      continue;
    }
    visited.add(stateKey);

    // Record this combination
    results.push({
      appliedBundles: current.appliedBundles,
      remainingNewValues: current.remainingTests
    });

    // Try to add each XChooseY bundle
    // Track which bundle IDs are already applied to prevent duplicates
    let appliedBundleIds = new Set(current.appliedBundles.map(b => b.bundleId));
    
    for (let xChooseYBundle of xChooseYBundles) {
      // Skip if this bundle is already applied (don't allow multiple instances of the same bundle)
      if (appliedBundleIds.has(xChooseYBundle.bundleId)) {
        continue;
      }
      
      let result = tryFormSingleXChooseYBundle(xChooseYBundle, current.remainingTests, testNodeList, testList);
      
      if (result) {
        let newState = {
          appliedBundles: [...current.appliedBundles, result.appliedBundle],
          remainingTests: result.remainingTests
        };
        
        let newBundleIds = newState.appliedBundles.map(b => b.bundleId).sort((a, b) => a - b).join(',');
        let newTestIdsKey = newState.remainingTests.sort((a, b) => a - b).join(',');
        let newStateKey = `${newBundleIds}|${newTestIdsKey}`;

        if (!visited.has(newStateKey)) {
          queue.push(newState);
        }
      }
    }
  }

  return results;
}

/**
 * Calculate price for each bundle combination and find the cheapest
 */
function findCheapestBundleCombination(
  combinations,
  originalTestIds,
  testNodeList,
  testList
) {
  let cheapest = null;
  let minPrice = Infinity;

  for (let combo of combinations) {
    let totalPrice = 0;

    // Add bundle prices
    for (let bundle of combo.appliedBundles) {
      totalPrice += bundle.finalPrice;
    }

    // Add remaining test prices
    // For XChooseY bundles, the appliedZoomers and appliedSupplements are already removed from remainingNewValues
    // Replace with parent tests
    let remainingTests = replaceWithParentTests(
      combo.remainingNewValues,
      testNodeList
    );
    handleBulletListPackageTests(testNodeList, remainingTests);

    for (let testId of remainingTests) {
      const test = testList.find(t => t.id === testId);
      if (test) {
        totalPrice += test.price;
      }
    }

    if (totalPrice < minPrice) {
      minPrice = totalPrice;
      cheapest = {
        appliedBundles: combo.appliedBundles,
        remainingNewValues: remainingTests, // Use replaced parent tests instead of original
        price: totalPrice
      };
    }
  }

  return cheapest || {
    appliedBundles: [],
    remainingNewValues: originalTestIds,
    price: 0
  };
}

/**
 * Calculate total price for applied bundles and remaining tests
 */
function calculateTotalPrice(
  appliedBundles,
  remainingTestIds,
  testNodeList,
  testList
) {
  let totalPrice = 0;

  // Add bundle prices
  for (let bundle of appliedBundles) {
    totalPrice += bundle.finalPrice;
  }

  // Add remaining test prices
  let remainingTests = replaceWithParentTests(remainingTestIds, testNodeList, true);
  handleBulletListPackageTests(testNodeList, remainingTests);

  for (let testId of remainingTests) {
    const test = testList.find(t => t.id === testId);
    if (test) {
      totalPrice += test.price;
    }
  }

  return totalPrice;
}

